import { Button } from '@cian/ui-kit';
import { IconActionCompareAdd16, IconActionCompareAdded16 } from '@cian/ui-kit-design-tokens/icons';
import { ComparisonContent } from '../../components/ComparisonContent';
import { ControlWrapper } from '../../components/ControlWrapper';
import { FakeControlWrapper } from '../../components/FakeControlWrapper';
import { ECategory, EStatus, IComparisonNotification } from '../../types';
import { ComparisonNotificationContainerLoadable } from '../ComparisonNotificationContainer';
import { useEventCallback } from '@cian/react-utils';
import { addToComparison } from '../../services/addToComparison';
import { deleteFromComparison } from '../../services/deleteFromComparison';
import { useState, useCallback } from 'react';
import { useApplicationContext } from '../../context';
import { useOffer, useTracking } from '../../hooks';

export const ComparisonContainer = () => {
  const { id, comparisonStatus, category } = useOffer();
  const { onComparisonAdd, onComparisonRemove } = useTracking();
  const context = useApplicationContext();

  const [{ status, description }, setComparisonData] = useState({
    status: comparisonStatus?.status,
    description: comparisonStatus?.description || '',
  });

  const [notification, setNotification] = useState<IComparisonNotification | null>(null);

  const addOfferToComparison = useEventCallback(async () => {
    const response = await addToComparison(context, { offerId: id, category: category as ECategory });

    if (!response) {
      return;
    }

    onComparisonAdd();

    const { text, success, comparisonUrl, offerId } = response;

    if (success) {
      setNotification({
        text,
        url: comparisonUrl,
        offerId,
      });

      setComparisonData({
        status: EStatus.Added,
        description: 'Добавлено в сравнение',
      });
    }
  });

  const deleteOfferFromComparison = useEventCallback(async () => {
    const response = await deleteFromComparison(context, { offerId: id });

    if (!response) {
      return;
    }

    onComparisonRemove();

    const { text, comparisonUrl, offerId } = response;

    setNotification({
      text,
      url: comparisonUrl,
      offerId,
    });

    setComparisonData({
      status: EStatus.Available,
      description: 'Добавить к сравнению',
    });
  });

  const clearNotification = useCallback(() => {
    setNotification(null);
  }, []);

  if (!status) {
    return null;
  }

  return (
    <>
      <ControlWrapper visible>
        {status === EStatus.Unavailable && (
          <ComparisonContent tooltipText={description}>
            <FakeControlWrapper>
              <Button
                theme="fill_primary"
                data-name="ComparisonControl"
                size="XS"
                beforeIcon={<IconActionCompareAdd16 color="gray60_100" />}
                disabled
                fullWidth
              />
            </FakeControlWrapper>
          </ComparisonContent>
        )}

        {status === EStatus.Added && (
          <ComparisonContent tooltipText={description}>
            <Button
              onClick={deleteOfferFromComparison}
              theme="stroke_secondary"
              data-name="ComparisonControl"
              size="XS"
              beforeIcon={<IconActionCompareAdded16 color="icon-main-default" />}
            />
          </ComparisonContent>
        )}

        {status === EStatus.Available && (
          <ComparisonContent tooltipText={description}>
            <Button
              onClick={addOfferToComparison}
              theme="stroke_secondary"
              data-name="ComparisonControl"
              size="XS"
              beforeIcon={<IconActionCompareAdd16 color="gray60_100" />}
            />
          </ComparisonContent>
        )}
      </ControlWrapper>

      {notification && (
        <ComparisonNotificationContainerLoadable notification={notification} onNotificationClear={clearNotification} />
      )}
    </>
  );
};
